import React, { useEffect, useMemo, useRef, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import DataTable from '../DataTable';
import LineChart from '../charts/LineChart';
import LasLogChart from '../charts/LasLogChart';
import { formatCurrency, formatInt } from '../../utils/format';
import { assetCashflows, evaluateDeal, npv } from '../../engine/dealEngine';
import { fitExponential, forecastMonthly } from '../../engine/dca';
import { useAssistantPageExtras } from '../assistant/AssistantContext';
import { useAuth } from '../../auth/AuthContext';
import { assistantApi, wellNotesApi } from '../../auth/api';
import { resolveWell, trailingProduction, navigateToWell } from '../../utils/wellIdentity';
import WellSelector from '../WellSelector';
import { track } from '../../lib/track';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';

const KIND_LABEL = {
  'openhole-quad': 'Open-hole quad',
  gamma: 'Gamma',
  survey: 'Survey / gyro',
  mudlog: 'Mudlog',
  stimlog: 'Stimlog',
  'cased-cbl': 'Cased-hole CBL',
  porosity: 'Porosity',
  other: 'Other',
};

function buildInsights(resolved, econ, trail, dcaFit) {
  const { identity, asset, actuals, lasFiles } = resolved;
  const out = [];
  if (identity.isBoswell) {
    out.push({
      tone: 'warn',
      title: 'Package concentration',
      body: 'Boswell dominates PDP PV10 (~59%). Rate durability after the Apr 2026 acid job is the primary bid risk — challenge the seller qi on the Sensitivity desk before anchoring bid.',
    });
  }
  if (asset?.ref?.pv10 != null && asset.ref.pv10 < 0) {
    out.push({
      tone: 'warn',
      title: 'Negative seller PV10',
      body: `Oneline PV10 is ${formatCurrency(asset.ref.pv10)}. This well still contributes its full (negative) PV to package totals — it is not floored at zero. See Decision → Liability schedule for the full negative-PV / inactive-well register and suggested plug/ARO share.`,
    });
  }
  if (asset?.cfScale != null && Math.abs(asset.cfScale - 1) > 0.3) {
    out.push({
      tone: 'info',
      title: 'Calibration scale outlier',
      body: `cfScale = ${asset.cfScale.toFixed(3)} — stream CF was scaled materially to match seller oneline PV10. Treat decline shape as reconstructed, not independently audited.`,
    });
  }
  if (dcaFit) {
    out.push({
      tone: 'info',
      title: 'History-fit decline',
      body: `Exponential fit on available history: qi ≈ ${formatInt(dcaFit.qi)} Mcf/d, De ≈ ${dcaFit.DePct}%/yr (R² ${dcaFit.r2}, n=${dcaFit.n}). Compare to seller qi ${asset?.qiMcfd != null ? formatInt(asset.qiMcfd) : '—'} / De ${(asset?.deAnnual != null ? asset.deAnnual * 100 : null)?.toFixed?.(1) ?? '—'}%/yr.`,
    });
  }
  const life = actuals?.lifeOfWell;
  if (life?.cumGasMmcf != null) {
    out.push({
      tone: 'ok',
      title: 'Life-of-well production',
      body: `Gross cum gas ${formatInt(life.cumGasMmcf)} MMcf` +
        (life.firstProdDate ? ` since first prod ${life.firstProdDate.slice(0, 10)}` : '') +
        (life.producingMonths != null ? ` · ${life.producingMonths} producing months` : '') +
        ` (${life.basis || 'AC_PRODUCT'}). Not EUR / not WI-net.`,
    });
  }
  if (trail?.l12AvgMcfpd != null) {
    out.push({
      tone: 'ok',
      title: 'Trailing production',
      body: `L3 / L6 / L12 avg rate: ${formatInt(trail.l3AvgMcfpd)} / ${formatInt(trail.l6AvgMcfpd)} / ${formatInt(trail.l12AvgMcfpd)} Mcf/d (${trail.monthsAvailable} months of history in room).`,
    });
  } else if (asset && !actuals?.lifeOfWell?.cumGasMmcf && !actuals?.lifeOfWell?.firstProdDate) {
    out.push({
      tone: 'muted',
      title: 'No production history in room',
      body: 'No production history for this prop — first-prod / life-of-well cum unavailable. Forward economics still come from the seller oneline and streams. Do not treat EUR / econ life as historical cum.',
    });
  } else if (asset && actuals?.lifeOfWell && !actuals?.recentMonths?.length) {
    out.push({
      tone: 'ok',
      title: 'Life-of-well scalars available',
      body: 'First-prod and gross cum gas are in the package for this well. Full monthly/daily charts remain Boswell-dense; other wells show scalars + forward streams.',
    });
  }
  if (!lasFiles?.length) {
    out.push({
      tone: identity.isBoswell ? 'warn' : 'muted',
      title: 'No LAS in VDR index',
      body: identity.isBoswell
        ? 'Boswell has zero LAS in the evidence room — do not invent petrophysics; underwrite with DCA + ops evidence.'
        : 'No matching LAS files indexed for this well. Check Diligence evidence room for rasters / PDFs.',
    });
  } else {
    out.push({
      tone: 'ok',
      title: 'Log coverage',
      body: `${lasFiles.length} LAS file(s) matched — ${lasFiles
        .map((f) => KIND_LABEL[f.kind] || f.kind)
        .slice(0, 4)
        .join(', ')}${lasFiles.length > 4 ? '…' : ''}. Use for geology diligence; package value still hinges on Boswell durability.`,
    });
  }
  if (econ?.modelPv10 != null && asset?.ref?.pv10 != null) {
    const delta = econ.modelPv10 - asset.ref.pv10;
    out.push({
      tone: 'info',
      title: 'Model vs seller PV10',
      body: `Live model PV10 ${formatCurrency(econ.modelPv10)} vs seller oneline ${formatCurrency(asset.ref.pv10)} (Δ ${formatCurrency(delta)} at base assumptions).`,
    });
  }
  if (actuals?.stats?.acid) {
    const a = actuals.stats.acid;
    out.push({
      tone: 'warn',
      title: 'Acid step-change',
      body: `Post-acid last-30 avg ~${formatInt(a.last30AvgMcfpd ?? actuals.stats.peakRecentMcfpd)} Mcf/d; trough ~${formatInt(actuals.stats.troughMcfpd)} Mcf/d. Fade-to-trough is the key stress case.`,
    });
  }
  return out;
}

export default function WellDetail({
  wellId,
  dealPackage,
  mapData,
  geoscienceData,
  onNavigate,
}) {
  const auth = useAuth();
  const { deal } = useDeal();
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const [selectedLasId, setSelectedLasId] = useState(null);
  const [curveData, setCurveData] = useState(null);
  const [curveError, setCurveError] = useState(null);
  const [loadingCurves, setLoadingCurves] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [notesError, setNotesError] = useState(null);
  const [notesBusy, setNotesBusy] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);

  const resolved = useMemo(
    () =>
      resolveWell(wellId, {
        dealPackage,
        mapWells: mapData?.wells || [],
        geoscienceFiles: geoscienceData?.files || [],
      }),
    [wellId, dealPackage, mapData, geoscienceData],
  );

  const econ = useMemo(() => {
    if (!dealPackage || !resolved?.asset || !resolved?.stream?.months?.length) return null;
    const taxRate = dealPackage.meta?.taxRate ?? 0.075;
    const assumptions = { ...dealPackage.assumptions, hhBase: dealPackage.meta?.hhBase ?? 3.42 };
    const { cfs, netMcf, revenue, loe, realized } = assetCashflows(
      resolved.asset,
      resolved.stream.months,
      assumptions,
      taxRate,
    );
    const modelPv10 = npv(cfs, assumptions.discountRate ?? 0.1);
    const pkgEval = evaluateDeal(dealPackage);
    const live = pkgEval.assets?.find((a) => a.propnum === resolved.asset.propnum);
    return {
      modelPv10,
      liveAssetPv: live?.assetPv ?? modelPv10,
      netMcf,
      revenue,
      loe,
      realized,
      monthly: resolved.stream.months.map((m, i) => ({
        date: m.date,
        netMcf: m.netMcf,
        loe: m.loe,
        cf: cfs[i],
        mcfpd: m.netMcf != null ? m.netMcf / 30.4 : null,
      })),
    };
  }, [dealPackage, resolved]);

  const historyMonths = useMemo(() => {
    if (resolved?.actuals?.recentMonths?.length) {
      return resolved.actuals.recentMonths.map((m) => ({
        month: m.month,
        mcfpd: m.mcfpd,
        netMcf: m.mcfpd * 30.4,
      }));
    }
    // Fall back to first 12 forward months as rate proxy when no actuals
    if (econ?.monthly?.length) {
      return econ.monthly.slice(0, 12).map((m) => ({
        month: m.date?.slice(0, 7) || m.date,
        mcfpd: m.mcfpd,
        netMcf: m.netMcf,
        forecast: true,
      }));
    }
    return [];
  }, [resolved, econ]);

  const trail = useMemo(() => trailingProduction(historyMonths), [historyMonths]);

  const dcaFit = useMemo(() => {
    const annual = resolved?.actuals?.annual || [];
    if (annual.length >= 3) {
      const t0 = Number(annual[0].year);
      const points = annual
        .filter((r) => r.mcfpd > 0)
        .map((r) => ({ t: Number(r.year) - t0, q: r.mcfpd }));
      return fitExponential(points);
    }
    if (historyMonths.length >= 6 && !historyMonths[0]?.forecast) {
      const points = historyMonths.map((m, i) => ({ t: i / 12, q: m.mcfpd }));
      return fitExponential(points);
    }
    return null;
  }, [resolved, historyMonths]);

  const declineSeries = useMemo(() => {
    const ariesQi = resolved?.asset?.qiMcfd;
    const ariesDe = resolved?.asset?.deAnnual;
    const start = (resolved?.asset?.start || '2026-06-01').slice(0, 7);
    const aries =
      ariesQi != null && ariesDe != null
        ? forecastMonthly({ qi: ariesQi, Dannual: ariesDe, startDate: start, months: 60 })
        : [];
    const histFit =
      dcaFit != null
        ? forecastMonthly({
            qi: dcaFit.qi,
            Dannual: dcaFit.Dannual,
            startDate: start,
            months: 60,
          })
        : [];
    const byMonth = new Map();
    for (const r of aries) {
      byMonth.set(r.month, { month: r.month, aries: r.mcfpd, histFit: null });
    }
    for (const r of histFit) {
      const row = byMonth.get(r.month) || { month: r.month, aries: null, histFit: null };
      row.histFit = r.mcfpd;
      byMonth.set(r.month, row);
    }
    return [...byMonth.values()].slice(0, 60);
  }, [resolved, dcaFit]);

  const insights = useMemo(
    () => (resolved ? buildInsights(resolved, econ, trail, dcaFit) : []),
    [resolved, econ, trail, dcaFit],
  );

  const selectorWells = useMemo(() => {
    const fromMap = mapData?.wells || [];
    const fromAssets = (dealPackage?.assets || []).map((a) => ({
      lease: a.lease,
      api: a.api,
      propnum: a.propnum,
      status: a.resCat === '1PDP' ? 'PDP' : a.resCat || 'Modeled',
      wellType: a.wellType,
      county: a.county,
      modeled: true,
      pv10M: a.ref?.pv10 != null ? a.ref.pv10 / 1000 : null,
    }));
    const byApi = new Map();
    for (const w of [...fromMap, ...fromAssets]) {
      const k = String(w.api || w.propnum || w.lease);
      const prev = byApi.get(k) || {};
      byApi.set(k, { ...prev, ...w });
    }
    return [...byApi.values()];
  }, [dealPackage, mapData]);

  const siblings = useMemo(() => {
    const assets = dealPackage?.assets || [];
    return [...assets]
      .sort((a, b) => (b.ref?.pv10 || 0) - (a.ref?.pv10 || 0))
      .slice(0, 12);
  }, [dealPackage]);

  useEffect(() => {
    if (!resolved) return;
    setPageExtras({
      tab: 'well',
      wellId,
      lease: resolved.identity.lease,
      api: resolved.identity.api,
      propnum: resolved.identity.propnum,
      isBoswell: resolved.identity.isBoswell,
      refPv10: resolved.asset?.ref?.pv10,
      modelPv10: econ?.modelPv10,
      qiMcfd: resolved.asset?.qiMcfd,
      deAnnual: resolved.asset?.deAnnual,
      trail,
      lasCount: resolved.lasFiles.length,
      insights: insights.map((i) => i.title),
    });
    track('well_detail_viewed', {
      well_id: wellId,
      lease: resolved.identity.lease,
      api: resolved.identity.api,
      propnum: resolved.identity.propnum,
      has_trail: !!trail?.l12AvgMcfpd,
      has_life_of_well: !!resolved.actuals?.lifeOfWell?.cumGasMmcf,
      las_count: resolved.lasFiles?.length || 0,
    });
    return () => clearPageExtras();
  }, [resolved, wellId, econ, trail, insights, setPageExtras, clearPageExtras]);

  // Section visibility → well_detail_section_viewed (once per section per well).
  const seenSectionsRef = useRef(new Set());
  useEffect(() => {
    seenSectionsRef.current = new Set();
    if (!resolved) return undefined;
    const root = document.querySelector('.well-detail-page');
    if (!root) return undefined;
    const nodes = root.querySelectorAll('[data-well-section]');
    if (!nodes.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const section = entry.target.getAttribute('data-well-section');
          if (!section || seenSectionsRef.current.has(section)) continue;
          seenSectionsRef.current.add(section);
          track('well_detail_section_viewed', {
            section,
            well_id: wellId,
            lease: resolved.identity.lease,
          });
        }
      },
      { threshold: 0.35 },
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [resolved, wellId]);

  // Dwell on well detail when leaving.
  useEffect(() => {
    if (!resolved) return undefined;
    const started = Date.now();
    return () => {
      const ms = Date.now() - started;
      if (ms < 1000) return;
      track('well_detail_dwell_ended', {
        well_id: wellId,
        lease: resolved.identity.lease,
        duration_seconds: Math.round(ms / 100) / 10,
        sections_seen: [...seenSectionsRef.current],
      });
    };
  }, [resolved, wellId]);

  useEffect(() => {
    const first = resolved?.lasFiles?.find((f) => f.hasDisplay) || resolved?.lasFiles?.[0];
    setSelectedLasId(first?.id || null);
    setCurveData(null);
    setCurveError(null);
  }, [resolved?.identity?.api, resolved?.identity?.propnum, wellId]);

  useEffect(() => {
    if (!selectedLasId) return;
    const meta = resolved?.lasFiles?.find((f) => f.id === selectedLasId);
    if (!meta?.hasDisplay) {
      setCurveData(null);
      setCurveError(meta ? 'No downsampled curves for this file.' : null);
      return;
    }
    let cancelled = false;
    setLoadingCurves(true);
    setCurveError(null);
    fetch(dealDataUrl(deal, `las-curves/${selectedLasId}.json`, import.meta.env.BASE_URL), {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Curve JSON ${r.status}`);
        return r.json();
      })
      .then((payload) => {
        if (!cancelled) setCurveData(payload);
      })
      .catch((e) => {
        if (!cancelled) {
          setCurveData(null);
          setCurveError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCurves(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLasId, resolved, deal]);

  // Prefer a stable notes bucket once we have API/propnum so async map/LAS
  // enrichment does not migrate notes mid-session.
  const noteKeyRef = useRef(null);
  const preferredNoteKey =
    resolved?.identity?.api || resolved?.identity?.propnum || wellId || null;
  if (preferredNoteKey) {
    const looksStable =
      /^\d{8,}$/.test(String(preferredNoteKey)) || /^SALE\d+/i.test(String(preferredNoteKey));
    if (!noteKeyRef.current || looksStable) noteKeyRef.current = preferredNoteKey;
  }
  const noteKey = noteKeyRef.current || preferredNoteKey;

  useEffect(() => {
    if (!noteKey || !auth.isAuthed) return;
    let cancelled = false;
    wellNotesApi
      .list(noteKey)
      .then((res) => {
        if (!cancelled) setNotes(res.notes || []);
      })
      .catch((e) => {
        if (!cancelled) setNotesError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [noteKey, auth.isAuthed]);

  async function submitNote(e) {
    e.preventDefault();
    if (!noteBody.trim() || !noteKey) return;
    setNotesBusy(true);
    setNotesError(null);
    try {
      const res = await wellNotesApi.add(noteKey, noteBody.trim());
      setNotes((prev) => [res.note, ...prev]);
      setNoteBody('');
      track('well_note_added', { well_key: noteKey });
    } catch (err) {
      setNotesError(err.message);
    } finally {
      setNotesBusy(false);
    }
  }

  async function removeNote(id) {
    try {
      await wellNotesApi.remove(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      setNotesError(err.message);
    }
  }

  async function generateAiAssessment() {
    if (!resolved) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const question = `Write a concise underwriting assessment for well ${resolved.identity.lease} (API ${resolved.identity.api || 'n/a'}, propnum ${resolved.identity.propnum || 'n/a'}). Cover: (1) economics / PV, (2) production & decline, (3) log evidence if any, (4) risks and diligence asks. Be specific; flag gaps.`;
      const res = await assistantApi.chat({
        question,
        dashboard_key: 'well',
        data: {
          well: resolved.identity,
          asset: resolved.asset,
          trail,
          dcaFit,
          lasFiles: resolved.lasFiles.map((f) => ({
            id: f.id,
            kind: f.kind,
            fileName: f.fileName,
            hasDisplay: f.hasDisplay,
          })),
          insights,
          modelPv10: econ?.modelPv10,
          ref: resolved.asset?.ref,
        },
        history: [],
      });
      setAiText(res.answer);
      track('well_ai_assessment', { lease: resolved.identity.lease });
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiBusy(false);
    }
  }

  if (!dealPackage) {
    return (
      <div className="dashboard-page">
        <div className="loading-screen">
          <div className="loading-spinner" />
          <p>Loading well package…</p>
        </div>
      </div>
    );
  }

  if (!resolved || (!resolved.asset && !resolved.mapWell && !resolved.lasFiles.length)) {
    return (
      <WellNotFound wellId={wellId} onNavigate={onNavigate} selectorWells={selectorWells} />
    );
  }

  const idn = resolved.identity;
  const asset = resolved.asset;
  const ref = asset?.ref;
  const selectedLas = resolved.lasFiles.find((f) => f.id === selectedLasId);
  const life = resolved.actuals?.lifeOfWell || null;
  const firstProdDate =
    life?.firstProdDate ||
    resolved.actuals?.stats?.firstProdDate ||
    (resolved.actuals?.annual?.[0]?.year != null
      ? `${resolved.actuals.annual[0].year}-01-01`
      : null);
  const firstProdLabel = firstProdDate
    ? firstProdDate.length >= 10
      ? firstProdDate.slice(0, 10)
      : String(firstProdDate)
    : null;
  const firstProdSub = life?.firstProdSource
    ? life.firstProdSource
    : asset?.start
      ? `Econ START ${asset.start.slice(0, 10)}`
      : '—';
  const cumGasMmcf = life?.cumGasMmcf ?? resolved.actuals?.stats?.cumGasMmcf ?? null;

  return (
    <div className="dashboard-page well-detail-page">
      <div className="page-header">
        <div className="page-header-left" style={{ width: '100%' }}>
          <div className="well-detail-nav">
            <button type="button" className="btn-secondary well-back" onClick={() => onNavigate?.('wells')}>
              ← Wells
            </button>
            <button type="button" className="btn-secondary well-back" onClick={() => onNavigate?.('map')}>
              Map
            </button>
            {idn.isBoswell && (
              <button
                type="button"
                className="btn-secondary well-back"
                onClick={() => onNavigate?.('sensitivity')}
              >
                Boswell DCA
              </button>
            )}
          </div>
          <WellSelector wells={selectorWells} currentWellId={wellId || idn.api || idn.lease} />
          <h2 className="page-title" style={{ marginTop: 14 }}>
            {idn.lease}
          </h2>
          <p className="page-subtitle">
            {[idn.status, idn.wellType, idn.county && `${idn.county} Co.`]
              .filter(Boolean)
              .join(' · ')}
            {idn.api ? ` · API ${idn.api}` : ''}
            {asset?.propnum ? ` · ${asset.propnum}` : ''}
          </p>
          <div className="page-quote">
            <span className="page-quote-label">Well detail</span>
            <span>
              Economics from the seller oneline and live streams · production history where the data room provides it ·
              logs from Geoscience index · notes saved to your account
            </span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="Seller PV10"
          value={ref?.pv10 != null ? formatCurrency(ref.pv10) : '—'}
          sub={econ?.modelPv10 != null ? `Model ${formatCurrency(econ.modelPv10)}` : 'Unmodeled'}
          color={ref?.pv10 != null && ref.pv10 < 0 ? 'var(--red)' : 'var(--amber)'}
        />
        <KPICard
          label="Net reserves"
          value={asset?.netGasResMmcf != null ? `${formatInt(asset.netGasResMmcf)} MMcf` : '—'}
          sub={asset?.lifeYears != null ? `${asset.lifeYears} yr life` : '—'}
          color="var(--navy)"
        />
        <KPICard
          label="WI / NRI"
          value={
            idn.wi != null && idn.nri != null ? `${Number(idn.wi).toFixed(1)}% / ${Number(idn.nri).toFixed(1)}%` : '—'
          }
          sub={asset?.baseRealized != null ? `Realized $${asset.baseRealized.toFixed(2)}/Mcf` : '—'}
          color="var(--teal)"
        />
        <KPICard
          label="First production"
          value={firstProdLabel || (asset?.start ? asset.start.slice(0, 10) : '—')}
          sub={
            firstProdLabel
              ? firstProdSub
              : asset?.start
                ? 'Seller econ start (not first sales)'
                : '—'
          }
          color="var(--green)"
        />
      </KPIGrid>

      {(trail?.l3AvgMcfpd != null ||
        trail?.l6AvgMcfpd != null ||
        cumGasMmcf != null ||
        life?.producingMonths != null) && (
        <section className="dashboard-section" data-well-section="production_history">
          <div className="section-title">Production history</div>
          <p className="engine-note" style={{ marginTop: 0 }}>
            Trailing L3/L6/L12 from recent history · life-of-well cum is gross AC_PRODUCT gas when
            available (Boswell today). Do not confuse cum with EUR / net reserves.
          </p>
          <KPIGrid>
            <KPICard
              label="L3M avg"
              value={trail?.l3AvgMcfpd != null ? `${formatInt(trail.l3AvgMcfpd)} Mcf/d` : '—'}
              sub={trail?.l3VolMcf != null ? `${formatInt(trail.l3VolMcf)} Mcf` : ''}
              color="var(--navy)"
            />
            <KPICard
              label="L6M avg"
              value={trail?.l6AvgMcfpd != null ? `${formatInt(trail.l6AvgMcfpd)} Mcf/d` : '—'}
              sub={trail?.l6VolMcf != null ? `${formatInt(trail.l6VolMcf)} Mcf` : ''}
              color="var(--amber)"
            />
            <KPICard
              label="L12M avg"
              value={trail?.l12AvgMcfpd != null ? `${formatInt(trail.l12AvgMcfpd)} Mcf/d` : '—'}
              sub={trail?.l12VolMcf != null ? `${formatInt(trail.l12VolMcf)} Mcf` : ''}
              color="var(--green)"
            />
            <KPICard
              label="Life-of-well cum"
              value={cumGasMmcf != null ? `${formatInt(cumGasMmcf)} MMcf` : '—'}
              sub={
                life?.producingMonths != null
                  ? `${life.producingMonths} mo` +
                    (life.lastProdDate ? ` · thru ${life.lastProdDate.slice(0, 7)}` : '')
                  : trail?.monthsAvailable != null
                    ? `${trail.monthsAvailable} mo in room`
                    : 'Re-extract DealPackage for cum'
              }
              color="var(--teal)"
            />
          </KPIGrid>
        </section>
      )}

      <section className="dashboard-section" data-well-section="ai_insights">
        <div className="section-title">AI assessment & insights</div>
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Structured insights</div>
            <div className="chart-subtitle">Rule-based from package facts — always available</div>
            <ul className="well-insight-list">
              {insights.map((ins) => (
                <li key={ins.title} className={`well-insight well-insight-${ins.tone}`}>
                  <strong>{ins.title}</strong>
                  <p>{ins.body}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="chart-card">
            <div className="chart-title">AI underwriting note</div>
            <div className="chart-subtitle">Claude via desk assistant · citeable, not a reserve report</div>
            <button
              type="button"
              className="btn-primary"
              disabled={aiBusy}
              onClick={generateAiAssessment}
              style={{ marginBottom: 12 }}
            >
              {aiBusy ? 'Generating…' : aiText ? 'Regenerate assessment' : 'Generate assessment'}
            </button>
            {aiError && <p className="engine-note error">{aiError}</p>}
            {aiText ? (
              <div className="well-ai-prose">{aiText}</div>
            ) : (
              <p className="engine-note">
                Generates a well-scoped memo from live economics, trailing rates, DCA fit, and LAS
                inventory. You can also ask follow-ups in the assistant panel.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="dashboard-section" data-well-section="economics">
        <div className="section-title">Economics</div>
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Seller oneline & model</div>
            <DataTable
              columns={[
                { key: 'label', label: 'Metric' },
                { key: 'value', label: 'Value' },
              ]}
              data={[
                { label: 'PV10 (seller)', value: ref?.pv10 != null ? formatCurrency(ref.pv10) : '—' },
                { label: 'PV15 (seller)', value: ref?.pv15 != null ? formatCurrency(ref.pv15) : '—' },
                { label: 'PV20 (seller)', value: ref?.pv20 != null ? formatCurrency(ref.pv20) : '—' },
                {
                  label: 'Undisc. CF (seller)',
                  value: ref?.undiscCf != null ? formatCurrency(ref.undiscCf) : '—',
                },
                { label: 'Model PV10 (base)', value: econ?.modelPv10 != null ? formatCurrency(econ.modelPv10) : '—' },
                { label: 'cfScale', value: asset?.cfScale != null ? asset.cfScale.toFixed(4) : '—' },
                { label: 'Gas diff', value: asset?.gasDiff != null ? `$${asset.gasDiff.toFixed(2)}/Mcf` : '—' },
                { label: 'Shrink', value: asset?.shrink != null ? `${(asset.shrink * 100).toFixed(1)}%` : '—' },
                {
                  label: 'In package PV',
                  value:
                    ref?.pv10 != null && ref.pv10 < 0
                      ? 'Yes — negative included (not floored)'
                      : asset
                        ? 'Yes — full contribution'
                        : 'Not modeled',
                },
              ]}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">Forward net CF (monthly)</div>
            <div className="chart-subtitle">Seller stream × base realized · cfScale applied</div>
            {econ?.monthly?.length ? (
              <LineChart
                data={econ.monthly.slice(0, 60).map((m) => ({
                  month: m.date?.slice(0, 7) || m.date,
                  cf: m.cf,
                }))}
                xKey="month"
                series={[{ key: 'cf', label: 'Net CF $', color: '#1B355C', area: true }]}
                yFormat={(v) => formatCurrency(v)}
                heightRatio={0.42}
              />
            ) : (
              <p className="engine-note">No monthly stream for this well (unmodeled / PDSI / P&A).</p>
            )}
          </div>
        </div>
      </section>

      <section className="dashboard-section" data-well-section="decline">
        <div className="section-title">Production & decline</div>
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">
              {historyMonths[0]?.forecast ? 'Forward rate (proxy)' : 'Recent production'}
            </div>
            <div className="chart-subtitle">
              {historyMonths[0]?.forecast
                ? 'No production history in the package — showing seller net Mcf/d'
                : 'VDR actuals · Mcf/d'}
            </div>
            {historyMonths.length ? (
              <LineChart
                data={historyMonths}
                xKey="month"
                series={[{ key: 'mcfpd', label: 'Mcf/d', color: '#0F766E', area: true }]}
                yFormat={(v) => formatInt(v)}
                heightRatio={0.42}
              />
            ) : (
              <p className="engine-note">No production series available.</p>
            )}
            {resolved.actuals?.annual?.length > 0 && (
              <>
                <div className="chart-title" style={{ marginTop: 16 }}>
                  Annual history
                </div>
                <LineChart
                  data={resolved.actuals.annual.map((r) => ({
                    month: String(r.year),
                    mcfpd: r.mcfpd,
                  }))}
                  xKey="month"
                  series={[{ key: 'mcfpd', label: 'Mcf/d', color: '#F59E0B', area: false }]}
                  xType="number"
                  yFormat={(v) => formatInt(v)}
                  heightRatio={0.35}
                />
              </>
            )}
          </div>
          <div className="chart-card">
            <div className="chart-title">Decline curve</div>
            <div className="chart-subtitle">
              Seller exp
              {asset?.qiMcfd != null
                ? ` · qi ${formatInt(asset.qiMcfd)} · De ${(asset.deAnnual * 100).toFixed(1)}%/yr`
                : ''}
              {dcaFit ? ` · history-fit De ${dcaFit.DePct}%/yr (R² ${dcaFit.r2})` : ''}
            </div>
            {declineSeries.length ? (
              <LineChart
                data={declineSeries}
                xKey="month"
                series={[
                  { key: 'aries', label: 'Seller', color: '#1B355C', area: false },
                  ...(dcaFit
                    ? [{ key: 'histFit', label: 'History fit', color: '#F59E0B', dashed: true }]
                    : []),
                ]}
                yFormat={(v) => formatInt(v)}
                heightRatio={0.45}
              />
            ) : (
              <p className="engine-note">No decline parameters on this well.</p>
            )}
          </div>
        </div>
      </section>

      <section className="dashboard-section" data-well-section="logs">
        <div className="section-title">Log file analysis</div>
        {resolved.lasFiles.length === 0 ? (
          <div className="chart-card">
            <p className="engine-note">
              No LAS files matched this well in the Geoscience index.
              {idn.isBoswell
                ? ' Boswell has zero LAS in the VDR — underwrite with DCA and ops files in Diligence.'
                : ' Open Geoscience to browse Bald Prairie coverage, or Diligence for raster/PDF evidence.'}
            </p>
            <button type="button" className="btn-secondary" onClick={() => onNavigate?.('geoscience')}>
              Open Geoscience
            </button>
          </div>
        ) : (
          <div className="charts-grid charts-grid-2">
            <div className="chart-card">
              <div className="chart-title">LAS inventory</div>
              <DataTable
                columns={[
                  { key: 'kind', label: 'Kind', format: (v) => KIND_LABEL[v] || v },
                  { key: 'fileName', label: 'File' },
                  {
                    key: 'hasDisplay',
                    label: 'Curves',
                    format: (v) => (v ? 'Yes' : 'Meta'),
                  },
                ]}
                data={resolved.lasFiles}
                onRowClick={(row) => setSelectedLasId(row.id)}
                rowClassName={(row) => (row.id === selectedLasId ? 'dtable-row-selected' : '')}
                maxHeight={320}
              />
            </div>
            <div className="chart-card">
              <div className="chart-title">
                {selectedLas ? selectedLas.wellName || selectedLas.wellFolder : 'Log display'}
              </div>
              <div className="chart-subtitle">
                {selectedLas
                  ? `${selectedLas.fileName} · ${KIND_LABEL[selectedLas.kind] || selectedLas.kind}`
                  : 'Select a file'}
              </div>
              {loadingCurves && <p className="engine-note">Loading curves…</p>}
              {curveError && <p className="engine-note error">{curveError}</p>}
              {curveData?.depth?.length ? (
                <LasLogChart data={curveData} />
              ) : (
                !loadingCurves && !curveError && <p className="engine-note">No curve series to plot.</p>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="dashboard-section" data-well-section="notes">
        <div className="section-title">Team notes</div>
        <div className="chart-card">
          <div className="chart-subtitle" style={{ marginBottom: 12 }}>
            Saved for logged-in users · visible to the desk · key {noteKey}
          </div>
          <form className="well-notes-form" onSubmit={submitNote}>
            <textarea
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Diligence note, call with seller, P&A judgment…"
              rows={3}
              maxLength={8000}
            />
            <button type="submit" className="btn-primary" disabled={notesBusy || !noteBody.trim()}>
              {notesBusy ? 'Saving…' : 'Add note'}
            </button>
          </form>
          {notesError && <p className="engine-note error">{notesError}</p>}
          {notes.length === 0 ? (
            <p className="engine-note">No notes yet for this well.</p>
          ) : (
            <ul className="well-notes-list">
              {notes.map((n) => (
                <li key={n.id}>
                  <div className="well-note-meta">
                    <strong>{n.author_name}</strong>
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                    {(auth.user?.id === n.user_id || auth.isAdmin) && (
                      <button type="button" className="btn-secondary danger" onClick={() => removeNote(n.id)}>
                        Delete
                      </button>
                    )}
                  </div>
                  <p>{n.body}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Other modeled wells</div>
        <DataTable
          columns={[
            { key: 'lease', label: 'Lease' },
            { key: 'api', label: 'API' },
            {
              key: 'pv10',
              label: 'PV10',
              format: (_, row) =>
                row.ref?.pv10 != null ? formatCurrency(row.ref.pv10) : '—',
            },
            {
              key: 'netGasResMmcf',
              label: 'Net MMcf',
              format: (v) => (v != null ? formatInt(v) : '—'),
            },
          ]}
          data={siblings}
          onRowClick={(row) => navigateToWell(row)}
          rowClassName={(row) =>
            row.propnum === asset?.propnum || normalizeMatch(row, idn) ? 'dtable-row-selected' : ''
          }
          maxHeight={280}
        />
      </section>
    </div>
  );
}

function WellNotFound({ wellId, onNavigate, selectorWells }) {
  useEffect(() => {
    track('ui_blank_state', { surface: 'well_detail', reason: 'well_not_found', well_id: wellId });
  }, [wellId]);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <button type="button" className="btn-secondary well-back" onClick={() => onNavigate?.('wells')}>
            ← Wells
          </button>
          <h2 className="page-title">Well not found</h2>
          <p className="page-subtitle">No match for “{wellId}” in DealPackage / map / LAS index.</p>
          <div style={{ marginTop: 16, maxWidth: 560 }}>
            <WellSelector wells={selectorWells} currentWellId={wellId} autoFocus />
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeMatch(row, idn) {
  return (
    (idn.api && String(row.api).replace(/\D/g, '') === String(idn.api).replace(/\D/g, '')) ||
    row.lease === idn.lease
  );
}
